import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface CommandItem {
  id: string;
  label: string;
  description?: string;
  group?: string;
  keywords?: string[];
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  items: CommandItem[];
  onSelect: (item: CommandItem) => void;
  emptyLabel?: string;
  placeholder?: string;
  onQueryChange?: (query: string) => void;
}

const DEFAULT_EMPTY = "No results";
const DEFAULT_PLACEHOLDER = "Type to search…";

export function CommandPalette({
  open,
  onClose,
  items,
  onSelect,
  emptyLabel = DEFAULT_EMPTY,
  placeholder = DEFAULT_PLACEHOLDER,
  onQueryChange,
}: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const onCloseRef = useRef(onClose);
  const onSelectRef = useRef(onSelect);
  const onQueryChangeRef = useRef(onQueryChange);
  onCloseRef.current = onClose;
  onSelectRef.current = onSelect;
  onQueryChangeRef.current = onQueryChange;

  useEffect(() => {
    onQueryChangeRef.current?.(query);
  }, [query]);

  const filtered = useMemo(() => filterItems(items, query), [items, query]);

  /* Reset highlight when the visible list shrinks/changes. */
  useEffect(() => {
    setHighlight(0);
  }, [query]);

  /* Clear query on close so next open starts fresh. */
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  /* Body marker + escape handler. */
  useEffect(() => {
    if (!open) return;
    document.body.setAttribute("data-command-palette-open", "true");

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.removeAttribute("data-command-palette-open");
    };
  }, [open]);

  /* Autofocus input when opened. */
  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  function handleInputKey(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (filtered.length === 0) return;
      setHighlight((h) => nextHighlight(h, filtered.length, 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (filtered.length === 0) return;
      setHighlight((h) => nextHighlight(h, filtered.length, -1));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const item = filtered[highlight];
      if (item) onSelectRef.current(item);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center p-6 sm:pt-24">
      <div
        data-testid="command-palette-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="relative w-full max-w-xl overflow-hidden rounded-fc-card border border-fc-border bg-fc-raised text-fc-text shadow-[0_20px_40px_rgba(0,0,0,0.5)]"
      >
        <div className="border-b border-fc-border">
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-autocomplete="list"
            aria-controls="command-palette-list"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleInputKey}
            placeholder={placeholder}
            className="w-full bg-transparent px-5 py-4 text-[15px] text-fc-text outline-none placeholder:text-fc-text-faint"
          />
        </div>

        {filtered.length === 0 ? (
          <div
            role="status"
            aria-label={emptyLabel}
            className="px-5 py-10 text-center text-[13px] text-fc-text-dim"
          >
            {emptyLabel}
          </div>
        ) : (
          <ul
            id="command-palette-list"
            role="listbox"
            className="max-h-[360px] overflow-y-auto py-2"
          >
            {filtered.map((item, i) => {
              const selected = i === highlight;
              return (
                <li
                  key={item.id}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => onSelect(item)}
                  className={cx(
                    "flex cursor-pointer flex-col gap-0.5 px-5 py-2.5 text-[14px]",
                    selected
                      ? "bg-fc-overlay text-fc-text"
                      : "text-fc-text-secondary hover:bg-fc-overlay hover:text-fc-text"
                  )}
                >
                  <span className="font-medium">{item.label}</span>
                  {item.description ? (
                    <span className="text-[12px] text-fc-text-dim">
                      {item.description}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * Substring match on label + keywords (case-insensitive). Exported for
 * unit-test reuse; production code should route through the component.
 */
export function filterItems(items: CommandItem[], query: string): CommandItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((it) => {
    if (it.label.toLowerCase().includes(q)) return true;
    if (it.keywords?.some((k) => k.toLowerCase().includes(q))) return true;
    return false;
  });
}

export function nextHighlight(current: number, count: number, delta: number): number {
  if (count === 0) return 0;
  const next = (current + delta) % count;
  return next < 0 ? next + count : next;
}

function cx(...parts: Array<string | false | undefined | null>): string {
  return parts.filter(Boolean).join(" ");
}
